import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Calendar, 
  MapPin, 
  ArrowLeft, 
  ArrowRight,
  Loader2,
  Check 
} from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import CountdownTimer from '../components/CountdownTimer';
import RouteMap from '../components/RouteMap';
import { isVideoMedia } from '../utils/media';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const parseRallyDate = (dateString) => {
  const match = dateString?.match(/([A-Za-z]+)\s+(\d+)(?:-\d+)?,?\s*(\d{4})/);
  if (match) {
    const [, month, day, year] = match;
    return new Date(`${month} ${day}, ${year}`);
  }
  return null;
};

const RallyDetail = () => {
  const { rallyId } = useParams();
  const [rally, setRally] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRally = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/rallies/${rallyId}`);
        setRally(response.data);
      } catch (error) {
        toast.error('Failed to load rally details');
      } finally {
        setLoading(false);
      }
    };
    fetchRally();
  }, [rallyId]);

  const targetDate = rally ? parseRallyDate(rally.dates) : null;
  const showCountdown = rally && (rally.status === 'open' || rally.status === 'upcoming') && targetDate && targetDate > new Date();

  const getStatusBadge = (status) => {
    switch (status) {
      case 'open':
        return <span className="badge-open px-4 py-2 text-sm font-['Oswald'] tracking-wider">OPEN FOR REGISTRATION</span>;
      case 'completed':
        return <span className="badge-completed px-4 py-2 text-sm font-['Oswald'] tracking-wider">COMPLETED</span>;
      case 'coming_soon':
        return <span className="badge-coming-soon px-4 py-2 text-sm font-['Oswald'] tracking-wider">COMING SOON</span>;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A]">
        <Navigation />
        <div className="pt-32 text-center">
          <Loader2 className="w-8 h-8 text-[#E8651A] animate-spin mx-auto" />
          <p className="text-[#A19D98] mt-4">Loading rally details...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!rally) {
    return (
      <div className="min-h-screen bg-[#0A0A0A]">
        <Navigation />
        <div className="pt-32 text-center px-6">
          <h1 className="font-['Oswald'] text-3xl text-[#F2EDE6] mb-4">Rally Not Found</h1>
          <Link to="/rallies" className="btn-primary">View All Rallies</Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div data-testid="rally-detail-page" className="min-h-screen bg-[#0A0A0A]">
      <Navigation />

      {/* Hero */}
      <section className="relative h-[50vh] pt-20">
        <div className="absolute inset-0">
          {isVideoMedia(rally.image_url, rally.image_media_type) ? (
            <video
              src={rally.image_url}
              className="w-full h-full object-cover"
              autoPlay
              muted
              loop
              playsInline
            />
          ) : (
            <img
              src={rally.image_url || 'https://images.unsplash.com/photo-1610809589386-9ea41901eb54?w=1200'}
              alt={rally.name}
              className="w-full h-full object-cover"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/60 to-transparent" />
        </div>

        <div className="relative z-10 h-full flex flex-col justify-end px-6 pb-12 max-w-7xl mx-auto">
          <Link to="/rallies" className="text-[#A19D98] hover:text-[#E8651A] flex items-center gap-2 mb-4">
            <ArrowLeft size={16} />
            Back to Rallies
          </Link>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {getStatusBadge(rally.status)}
            <h1 className="font-['Oswald'] text-4xl sm:text-5xl md:text-6xl text-[#F2EDE6] mt-4 mb-4">
              {rally.name}
            </h1>
            <div className="flex flex-wrap gap-4 text-[#A19D98]">
              <div className="flex items-center gap-2">
                <Calendar size={18} />
                <span>{rally.dates}</span>
              </div>
              {rally.route && (
                <div className="flex items-center gap-2">
                  <MapPin size={18} />
                  <span>{rally.route}</span>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-12">
            {/* Description */}
            {rally.description && (
              <div>
                <h2 className="font-['Oswald'] text-2xl text-[#F2EDE6] mb-4">ABOUT THIS RALLY</h2>
                <p className="text-[#A19D98] leading-relaxed">{rally.description}</p>
              </div>
            )}

            {/* What's Included */}
            {rally.includes && rally.includes.length > 0 && (
              <div>
                <h2 className="font-['Oswald'] text-2xl text-[#F2EDE6] mb-4">WHAT'S INCLUDED</h2>
                <ul className="space-y-3">
                  {rally.includes.map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-[#A19D98]">
                      <Check className="w-5 h-5 text-[#E8651A] flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Route Map */}
            {rally.route && (
              <div>
                <h2 className="font-['Oswald'] text-2xl text-[#F2EDE6] mb-4">THE ROUTE</h2>
                <RouteMap rally={rally} />
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Countdown */}
            {showCountdown && (
              <div className="bg-[#141414] border border-[#2A2A2A] p-6">
                <p className="text-[#A19D98] text-sm text-center mb-4">RALLY STARTS IN</p>
                <CountdownTimer targetDate={targetDate} />
              </div>
            )}

            {/* Price & CTA */}
            <div className="bg-[#141414] border border-[#2A2A2A] p-6">
              {rally.price && (
                <div className="text-center mb-6">
                  <p className="text-[#A19D98] text-sm">PRICE</p>
                  <p className="font-['Oswald'] text-3xl text-[#F2EDE6]">{rally.price}</p>
                </div>
              )}

              {(rally.status === 'open' || rally.status === 'upcoming') && (
                <div className="space-y-3">
                  <Link
                    to={`/rallies/${rally.id}/register`}
                    data-testid="rally-detail-register-btn"
                    className="btn-primary w-full flex items-center justify-center gap-2"
                  >
                    Register for this Rally
                    <ArrowRight size={18} />
                  </Link>
                </div>
              )}

              {rally.status === 'coming_soon' && (
                <Link
                  to={`/rallies?notify=${rally.id}`}
                  className="btn-primary w-full flex items-center justify-center"
                >
                  Notify Me When Open
                </Link>
              )}
            </div>

            {/* Share */}
            <div className="bg-[#141414] border border-[#2A2A2A] p-6">
              <p className="text-[#A19D98] text-sm mb-4">SHARE THIS RALLY</p>
              <div className="flex gap-2">
                <a
                  href={`https://twitter.com/intent/tweet?text=Check out ${rally.name}!&url=${window.location.href}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary flex-1 text-center text-sm"
                >
                  Twitter
                </a>
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${window.location.href}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary flex-1 text-center text-sm"
                >
                  Facebook
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default RallyDetail;
